import {
  Settings,
  Users,
  FileText,
  DollarSign,
  ShieldAlert,
  Layers,
} from 'lucide-react'
import { RolePageData } from '../types'

export const operationsData: RolePageData = {
  id: 'operations',
  slug: 'operations',
  label: 'Operations',
  icon: Settings,
  title: 'Make procurement predictable and controlled',
  description:
    'One supplier, clear approval flows, and no messy invoices or vendor chaos.',

  context:
    'You own process, suppliers, and invoices. Merch is noise, not strategy — and you need to minimize risk.',

  jobToBeDone: 'Make this predictable, controlled, and easy to manage.',

  dailyReality: [
    'Multiple teams requesting things',
    'Scattered suppliers creating complexity',
    'Budget pressure from leadership',
    'Approval flows that need to be followed',
  ],

  painPoints: [
    {
      title: 'No overview or control',
      description:
        "Different teams ordering from different vendors. You don't know what's being spent or by whom.",
      icon: Layers,
    },
    {
      title: 'Too many vendors',
      description:
        'Each vendor means another contract, another invoice, another relationship to manage.',
      icon: Users,
    },
    {
      title: 'Messy invoicing',
      description:
        'Inconsistent pricing, surprise fees, and invoices that are hard to reconcile with budgets.',
      icon: FileText,
    },
    {
      title: 'Inconsistent pricing',
      description:
        "Prices change between orders. It's hard to budget or compare when nothing is predictable.",
      icon: DollarSign,
    },
  ],

  hiddenFears: [
    'This will create exceptions to our process',
    'This will be hard to control',
    "This won't scale",
  ],

  esgAngle: {
    level: 'high',
    description:
      'High sensitivity, especially in EU. Cares about compliance and defensibility for audits and stakeholders.',
  },

  whatTheyValue: ['Control', 'Clarity', 'Repeatability', 'Documentation'],

  languageTheyUse: [
    'Process',
    'Oversight',
    'Approval',
    'Cost control',
    'Compliance',
  ],

  convincers: [
    {
      quote: 'One supplier for everything',
      description:
        'Consolidate all merch under one account. One relationship, one invoice, one process.',
    },
    {
      quote: 'Clear approval steps',
      description:
        'Define who can order what, with built-in approval workflows that match your process.',
    },
    {
      quote: 'Nothing moves without approval',
      description:
        'Every order requires explicit sign-off. No rogue purchases, no surprises.',
    },
    {
      quote: 'Clean, predictable invoicing',
      description:
        'Transparent pricing, consolidated invoices, and no hidden fees. Easy to reconcile.',
    },
  ],

  dealBreakers: [
    {
      title: 'Vague pricing',
      description:
        'If you can\'t get clear pricing upfront, you can\'t budget or control costs.',
    },
    {
      title: 'Unclear responsibility',
      description:
        "You need to know exactly who's accountable when something goes wrong.",
    },
    {
      title: 'Too much flexibility without control',
      description:
        'Options are fine, but not if they create chaos. You need guardrails.',
    },
  ],

  testimonial: {
    headline: 'Finally, procurement I can actually control',
    name: 'Johan Eriksson',
    title: 'Head of Operations, ScaleUp AB',
    image:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
  },

  faqs: [
    {
      question: 'Can we set up approval workflows?',
      answer:
        'Yes. You can define who can order, spending limits, and required approvals. Nothing gets ordered without the right sign-offs.',
    },
    {
      question: 'How does invoicing work?',
      answer:
        'We provide consolidated monthly invoices with clear line items. You can also set up cost centers and PO numbers for easy reconciliation.',
    },
    {
      question: 'Can we get volume discounts?',
      answer:
        'Yes. As you consolidate more ordering with us, pricing improves. We offer annual agreements with predictable pricing.',
    },
    {
      question: 'What compliance documentation do you provide?',
      answer:
        'We can provide supplier certifications, sustainability documentation, and audit-ready records for all orders.',
    },
  ],

  cta: {
    primary: 'See how merch procurement works',
    secondary: 'Get a controlled merch quote',
    promptTemplate:
      "Hi! I need to consolidate our merch procurement under one supplier with clear approval flows and predictable invoicing. Can you show me how your system works?",
  },
}
